import React, { useState, useEffect } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Wraps a paid module's page. Checks entitlement on mount; if the firm
 * hasn't subscribed, blurs the real content and shows a lock overlay.
 *
 * Usage:
 *   <ModuleGate moduleKey="mca_workspace" moduleName="MCA Workspace" token={token}>
 *     <MCAWorkspacePage token={token} ... />
 *   </ModuleGate>
 */
export default function ModuleGate({ moduleKey, moduleName, token, children }) {
  const [status, setStatus] = useState('checking'); // checking | active | locked

  const [lockReason, setLockReason] = useState(null); // 'firm_not_subscribed' | 'user_not_granted' | null

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    fetch(`${BASE}/api/modules/${moduleKey}/access`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal
    })
      .then(r => {
        if (!r.ok) throw new Error(`Module access check failed: HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        if (cancelled) return;
        setLockReason(d.reason || (d.is_active ? null : 'firm_not_subscribed'));
        setStatus(d.is_active ? 'active' : 'locked');
      })
      .catch(err => {
        console.error('[ModuleGate] access check failed:', err.message);
        if (!cancelled) { setLockReason('firm_not_subscribed'); setStatus('locked'); }
      })
      .finally(() => clearTimeout(timeoutId));

    return () => { cancelled = true; controller.abort(); clearTimeout(timeoutId); };
  }, [moduleKey, token]);

  if (status === 'checking') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#0f1f45', fontSize: 14 }}>
        Checking module access…
      </div>
    );
  }

  if (status === 'active') return children;

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      <div style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
        {children}
      </div>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '36px 40px', maxWidth: 420, textAlign: 'center', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          {lockReason === 'user_not_granted' ? (
            <>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0f1f45', margin: '0 0 8px' }}>
                You don't have access to {moduleName || 'this module'}
              </h3>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 22px', lineHeight: 1.5 }}>
                Your firm is subscribed, but your admin hasn't granted you access yet. Ask them to enable it under Settings → Workspace Access.
              </p>
            </>
          ) : (
            <>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0f1f45', margin: '0 0 8px' }}>
                You haven't subscribed to {moduleName || 'this module'}
              </h3>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 22px', lineHeight: 1.5 }}>
                Upgrade your plan or contact us to activate this module for your firm.
              </p>
              <button onClick={() => window.location.href = '/upgrade'}
                style={{ padding: '11px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Contact Us to Upgrade
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}